/**
 * Resolves the tenant from the host at boot and gates the rest of the app.
 *
 * Runs above AuthGate so an invalid org subdomain short-circuits to a branded
 * 404 *before* any login UI. Stores the resolved org slug (via auth/session) so
 * the X-Org-Slug login header binds the session to this org.
 *
 *   admin subdomain        -> render app (no org, fallback brand)
 *   real org subdomain      -> fetch public org -> render app with its branding
 *   unknown org subdomain   -> 404 (strict)
 *   apex / reserved / none  -> 404 (strict)
 *   backend unreachable/5xx  -> transient error with retry (not a hard 404)
 */
import { Box, Button, Stack, Typography } from '@mui/material'
import { FC, PropsWithChildren, createContext, useEffect, useState } from 'react'

import { setOrgSlug } from '../auth/session'
import LoadingIndicator from '../components/common/LoadingIndicator'

import NotFoundPage from './NotFoundPage'
import { PublicOrg, getPublicOrgResult } from './publicOrg'
import { ADMIN_SLUG, getSlugFromHost, resolveTenantKind, MOVED_TO_VANITY } from './tenant'

export type Tenant = { kind: 'admin' } | { kind: 'org'; org: PublicOrg }

/** null while unresolved or for non-org tenants; useBrand falls back accordingly. */
export const TenantContext = createContext<Tenant | null>(null)

type Phase =
  | { status: 'loading' }
  | { status: 'ready'; tenant: Tenant }
  | { status: 'not_found' }
  | { status: 'error' }

const FullScreen: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    }}
  >
    {children}
  </Box>
)

const TenantError: FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <FullScreen>
    <Stack spacing={2} alignItems="center" maxWidth={440}>
      <Typography sx={{ fontWeight: 600 }} variant="h6">
        Couldn’t load this organization
      </Typography>
      <Typography color="text.secondary" textAlign="center">
        We couldn’t reach the server. Please check your connection and try again.
      </Typography>
      <Button variant="contained" type="button" onClick={onRetry}>
        Retry
      </Button>
    </Stack>
  </FullScreen>
)

const TenantProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [phase, setPhase] = useState<Phase>({ status: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    const host = typeof window !== 'undefined' ? window.location.hostname : null

    // Permanently moved to a vanity domain (e.g. aristotle.itg.ai -> aristotle.ai).
    const movedTo = host ? MOVED_TO_VANITY[host.toLowerCase()] : undefined
    if (movedTo) {
      window.location.replace(movedTo + window.location.pathname + window.location.search + window.location.hash)
      return
    }

    const slug = getSlugFromHost(host)
    const kind = resolveTenantKind(slug)

    if (kind === 'admin') {
      // Bind login to the reserved admin portal (backend gates this to SUPER_ADMIN).
      setOrgSlug(ADMIN_SLUG)
      setPhase({ status: 'ready', tenant: { kind: 'admin' } })
      return
    }
    if (kind === 'none') {
      setPhase({ status: 'not_found' })
      return
    }

    setPhase({ status: 'loading' })
    getPublicOrgResult(slug as string).then((res) => {
      if (cancelled) return
      if (res.status === 'found') {
        setOrgSlug(res.org.slug)
        setPhase({ status: 'ready', tenant: { kind: 'org', org: res.org } })
      } else if (res.status === 'not_found') {
        setPhase({ status: 'not_found' })
      } else {
        setPhase({ status: 'error' })
      }
    })

    return () => {
      cancelled = true
    }
  }, [reloadKey])

  if (phase.status === 'loading') {
    return (
      <FullScreen>
        <LoadingIndicator size="large" />
      </FullScreen>
    )
  }
  if (phase.status === 'not_found') {
    return (
      <TenantContext.Provider value={null}>
        <NotFoundPage />
      </TenantContext.Provider>
    )
  }
  if (phase.status === 'error') {
    return (
      <TenantContext.Provider value={null}>
        <TenantError onRetry={() => setReloadKey((k) => k + 1)} />
      </TenantContext.Provider>
    )
  }
  return <TenantContext.Provider value={phase.tenant}>{children}</TenantContext.Provider>
}

export default TenantProvider
